import { FC, useEffect, useState } from 'react';

import { ErrorBlock } from '@telialt/core-nextjs/src/components/Error/ErrorBlock';
import { useLoadCompareProducts } from '@telialt/core-search/src/hooks/useLoadCompareProducts';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import { useNavigationOffset } from '@telialt/utils/src/NavigationUtils/useNavigationOffset';
import { isDesktopRes } from '@telialt/utils/src/responsive';
import { getSegment } from '@telialt/utils/src/segment';

import { useLoadCompareClassifications } from '../../../hooks/useLoadCompareClassifications';
import { useProductsCompare } from '../../../hooks/useProductsCompare';
import { filterOnlyDifferentFeatures } from '../../../utils';
import { ComparisonProductsRow } from './ComparisonProductsRow';
import { ComparisonRowsSection } from './ComparisonRowsSection';
import { ComparisonTableHead, ComparisonTableStickySpacer, StyledComparisonTable } from './ComparisonTable.styles';
import { ComparisonTableSkeleton } from './ComparisonTableSkeleton';

const STICKY_HEAD_DESKTOP_OFFSET = 320;
const STICKY_HEAD_DESKTOP_SPACER_OFFSET = 16;
const STICKY_HEAD_MOBILE_OFFSET = 100;

type Props = {
    categoryPath: string;
    categoryName?: string;
};

export const ComparisonTable: FC<Props> = ({ categoryPath, categoryName }) => {
    const { offset: stickyNavigationOffset } = useNavigationOffset({
        enabled: true,
    });
    const topOffset = stickyNavigationOffset;
    const [isHeaderMinimal, setIsHeaderMinimal] = useState(false);
    const [showDifferencesOnly, setShowDifferencesOnly] = useState(true);
    const [isRedirectLoading, setIsRedirectLoading] = useState(false);

    const { isComparedProductsLoading, comparedProducts, compareProductsLimit, removeComparedProduct } =
        useProductsCompare(categoryPath);

    const productIds = comparedProducts.map(({ id }) => id);
    const segment = getSegment(categoryPath);

    const {
        isLoading: isProductsLoading,
        data: products,
        error: productsError,
    } = useLoadCompareProducts({
        productIds,
        segment,
        isEnabled: productIds.length > 0,
    });

    const {
        isFetching: isClassificationsLoading,
        data: productsClassifications,
        error: classificationsError,
    } = useLoadCompareClassifications(segment, productIds);
    useLogError(classificationsError, DDComponent.EshopComparison);

    useEffect(() => {
        const handleScroll = () => {
            const documentBodyTop = document.body.getBoundingClientRect().top + topOffset;
            setIsHeaderMinimal(
                documentBodyTop <= (isDesktopRes() ? -STICKY_HEAD_DESKTOP_OFFSET : -STICKY_HEAD_MOBILE_OFFSET),
            );
        };
        const resizeObserver = new ResizeObserver(handleScroll);
        resizeObserver.observe(document.body);
        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('scroll', handleScroll);
        };
    }, [topOffset, isProductsLoading, isClassificationsLoading]);

    if (productsError || classificationsError) {
        return <ErrorBlock />;
    }

    if (
        isRedirectLoading ||
        isComparedProductsLoading ||
        (productIds.length > 0 && (isProductsLoading || isClassificationsLoading))
    ) {
        return <ComparisonTableSkeleton />;
    }

    if (!comparedProducts?.length || !products?.items?.length || !productsClassifications?.length) {
        return null;
    }

    const handleRemoveProduct = (productCode: string) => {
        removeComparedProduct(productCode);
        if (comparedProducts.length === 1) {
            setIsRedirectLoading(true);
            window.location.href = categoryPath;
        }
    };

    const handleOnlyDifferencesCheck = (checked: boolean) => {
        setShowDifferencesOnly(checked);
    };

    const differentClassifications = filterOnlyDifferentFeatures(productsClassifications);

    const classificationsToShow =
        differentClassifications.length > 0 && showDifferencesOnly ? differentClassifications : productsClassifications;

    return (
        <>
            {isHeaderMinimal && (
                <ComparisonTableStickySpacer
                    $desktopHeight={STICKY_HEAD_DESKTOP_OFFSET + STICKY_HEAD_DESKTOP_SPACER_OFFSET}
                    $mobileHeight={STICKY_HEAD_MOBILE_OFFSET}
                />
            )}
            <StyledComparisonTable>
                <ComparisonTableHead $topOffset={topOffset} $isSticky={isHeaderMinimal}>
                    <ComparisonProductsRow
                        categoryPath={categoryPath}
                        categoryName={categoryName}
                        isHeaderMinimal={isHeaderMinimal}
                        showDifferencesOnly={showDifferencesOnly}
                        onDifferencesOnlyChange={handleOnlyDifferencesCheck}
                        isDifferencesOnlyDisabled={differentClassifications.length === 0}
                        productCodes={productIds}
                        products={products?.items}
                        productsLimit={compareProductsLimit}
                        onRemoveProduct={handleRemoveProduct}
                    />
                </ComparisonTableHead>
                {classificationsToShow.map((classification) => (
                    <ComparisonRowsSection
                        key={classification.code}
                        classification={classification}
                        productsLimit={compareProductsLimit}
                        productCodes={productIds}
                    />
                ))}
            </StyledComparisonTable>
        </>
    );
};
